import { useMemo } from 'react';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import {
  hasAnyNumericBreedAverage,
  isBlackRedAngusStrain,
  mergeEbvDisplayOptions,
  type EbvDisplayOptionsJson,
  type EffectiveEbvDisplayOptions,
} from '@/components/publicSearch/animal/ebv/ebvProviderCapabilities';
import { parseEbvAverage } from '@/hooks/publicSearch/ebv/ebvColumns';

type SocietySettingLike = {
  societySettingType?: string | null;
  value?: string | null;
};

function parseEbvDisplayOptionsSetting(
  settings: SocietySettingLike[] | undefined,
): EbvDisplayOptionsJson | null {
  if (!settings?.length) return null;

  const setting = settings.find(
    (s) => s?.societySettingType === SOCIETY_SETTING_TYPE.EBV_DISPLAY_OPTIONS,
  );
  if (!setting?.value) return null;

  try {
    return JSON.parse(setting.value) as EbvDisplayOptionsJson;
  } catch {
    return null;
  }
}

function parseHasBandArraySetting(
  settings: SocietySettingLike[] | undefined,
  societySettingType: string,
): boolean {
  if (!settings?.length) return false;

  const setting = settings.find(
    (s) => s?.societySettingType === societySettingType,
  );
  if (!setting?.value) return false;

  try {
    const parsed = JSON.parse(setting.value);
    return Array.isArray(parsed) && parsed.length > 0;
  } catch {
    return false;
  }
}

/**
 * Society EBV display profile from setting type 26: provider, strain mode,
 * i4 ebvBoxOptions flags, and resolved value label (EBV / EPD / ABV).
 * Percentile / average gates also use live setting 15/16/29 data shape so
 * label-only societies (null avgs, no P%/B%/R% bands) show values only.
 *
 * Capability matrix and flag meanings: {@link mergeEbvDisplayOptions} /
 * `ebvProviderCapabilities.ts` file header.
 */
export function useEbvDisplayOptions(): EffectiveEbvDisplayOptions {
  const settings = usePublicSearchContext((c) => c.society.settings);

  return useMemo(() => {
    const raw = parseEbvDisplayOptionsSetting(settings);
    const hasBrPercentileBands =
      isBlackRedAngusStrain(raw?.ebvStrainMode) &&
      parseHasBandArraySetting(
        settings,
        SOCIETY_SETTING_TYPE.EBV_PERCENTAGES_BR,
      );
    const hasPercentileBands = parseHasBandArraySetting(
      settings,
      SOCIETY_SETTING_TYPE.EBV_PERCENTAGES,
    );
    const hasNumericAverages = hasAnyNumericBreedAverage(
      parseEbvAverage(settings),
    );

    return mergeEbvDisplayOptions(raw, {
      hasBrPercentileBands,
      hasPercentileBands,
      hasAnyNumericBreedAverage: hasNumericAverages,
    });
  }, [settings]);
}
